/**
 * Generato automaticamente.
 * OpenAPI
 */
import { useMutation, useQuery } from 'react-query';
import type {
  MutationFunction,
  QueryFunction,
  QueryKey,
  UseMutationOptions,
  UseQueryOptions,
  UseQueryResult,
} from 'react-query';
import { apiClientRequest } from '../../../client/apiClient';
import type { ErrorType } from '../../../client/apiClient';
import type {
  GetTraduzioniParams,
  PostTraduzioniBody,
  PostTraduzioniParams,
  WsProblema,
} from '.././schemas';

type AwaitedInput<T> = PromiseLike<T> | T;

type Awaited<O> = O extends AwaitedInput<infer T> ? T : never;

// eslint-disable-next-line
type SecondParameter<T extends (...args: any) => any> = T extends (
  config: any,
  args: infer P,
) => any
  ? P
  : never;

/**
 * Ritorna le traduzioni relative a classe/oggetto, in formato csv.
 * @summary Elenco Traduzioni
 */
export const getTraduzioni = (
  params: GetTraduzioniParams,
  options?: SecondParameter<typeof apiClientRequest>,
  signal?: AbortSignal,
) => {
  return apiClientRequest<string>(
    { url: `/framework-cfg-domande/traduzioni`, method: 'get', params, signal },
    options,
  );
};

export const getGetTraduzioniQueryKey = (params: GetTraduzioniParams) =>
  [`/framework-cfg-domande/traduzioni`, ...(params ? [params] : [])] as const;

export const getGetTraduzioniQueryOptions = <
  TData = Awaited<ReturnType<typeof getTraduzioni>>,
  TError = ErrorType<WsProblema>,
>(
  params: GetTraduzioniParams,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getTraduzioni>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryOptions<Awaited<ReturnType<typeof getTraduzioni>>, TError, TData> & {
  queryKey: QueryKey;
} => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetTraduzioniQueryKey(params);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getTraduzioni>>> = ({
    signal,
  }) => getTraduzioni(params, requestOptions, signal);

  return { queryKey, queryFn, ...queryOptions };
};

export type GetTraduzioniQueryResult = NonNullable<
  Awaited<ReturnType<typeof getTraduzioni>>
>;
export type GetTraduzioniQueryError = ErrorType<WsProblema>;

/**
 * @summary Elenco Traduzioni
 */
export const useGetTraduzioni = <
  TData = Awaited<ReturnType<typeof getTraduzioni>>,
  TError = ErrorType<WsProblema>,
>(
  params: GetTraduzioniParams,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getTraduzioni>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions = getGetTraduzioniQueryOptions(params, options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
};

/**
 * Carica le traduzioni relative al modello e la lingua inserita, in formato csv.
 * @summary Carica Traduzioni
 */
export const postTraduzioni = (
  postTraduzioniBody: PostTraduzioniBody,
  params: PostTraduzioniParams,
  options?: SecondParameter<typeof apiClientRequest>,
) => {
  const formData = new FormData();
  formData.append('file', postTraduzioniBody.file);

  return apiClientRequest<string[]>(
    {
      url: `/framework-cfg-domande/traduzioni`,
      method: 'post',
      headers: { 'Content-Type': 'multipart/form-data' },
      data: formData,
      params,
    },
    options,
  );
};

export const getPostTraduzioniMutationOptions = <
  TError = ErrorType<WsProblema>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof postTraduzioni>>,
    TError,
    { data: PostTraduzioniBody; params: PostTraduzioniParams },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof postTraduzioni>>,
  TError,
  { data: PostTraduzioniBody; params: PostTraduzioniParams },
  TContext
> => {
  const { mutation: mutationOptions, request: requestOptions } = options ?? {};

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof postTraduzioni>>,
    { data: PostTraduzioniBody; params: PostTraduzioniParams }
  > = props => {
    const { data, params } = props ?? {};

    return postTraduzioni(data, params, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type PostTraduzioniMutationResult = NonNullable<
  Awaited<ReturnType<typeof postTraduzioni>>
>;
export type PostTraduzioniMutationBody = PostTraduzioniBody;
export type PostTraduzioniMutationError = ErrorType<WsProblema>;

/**
 * @summary Carica Traduzioni
 */
export const usePostTraduzioni = <
  TError = ErrorType<WsProblema>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof postTraduzioni>>,
    TError,
    { data: PostTraduzioniBody; params: PostTraduzioniParams },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}) => {
  const mutationOptions = getPostTraduzioniMutationOptions(options);

  return useMutation(mutationOptions);
};
/**
 * Ritorna la lista delle lingue disponibili.
 * @summary Lingue Disponibili
 */
export const getTraduzioniLingueDisponibili = (
  options?: SecondParameter<typeof apiClientRequest>,
  signal?: AbortSignal,
) => {
  return apiClientRequest<string[]>(
    {
      url: `/framework-cfg-domande/traduzioni/lingue-disponibili`,
      method: 'get',
      signal,
    },
    options,
  );
};

export const getGetTraduzioniLingueDisponibiliQueryKey = () =>
  [`/framework-cfg-domande/traduzioni/lingue-disponibili`] as const;

export const getGetTraduzioniLingueDisponibiliQueryOptions = <
  TData = Awaited<ReturnType<typeof getTraduzioniLingueDisponibili>>,
  TError = ErrorType<unknown>,
>(options?: {
  query?: UseQueryOptions<
    Awaited<ReturnType<typeof getTraduzioniLingueDisponibili>>,
    TError,
    TData
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}): UseQueryOptions<
  Awaited<ReturnType<typeof getTraduzioniLingueDisponibili>>,
  TError,
  TData
> & { queryKey: QueryKey } => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getGetTraduzioniLingueDisponibiliQueryKey();

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getTraduzioniLingueDisponibili>>
  > = ({ signal }) => getTraduzioniLingueDisponibili(requestOptions, signal);

  return { queryKey, queryFn, ...queryOptions };
};

export type GetTraduzioniLingueDisponibiliQueryResult = NonNullable<
  Awaited<ReturnType<typeof getTraduzioniLingueDisponibili>>
>;
export type GetTraduzioniLingueDisponibiliQueryError = ErrorType<unknown>;

/**
 * @summary Lingue Disponibili
 */
export const useGetTraduzioniLingueDisponibili = <
  TData = Awaited<ReturnType<typeof getTraduzioniLingueDisponibili>>,
  TError = ErrorType<unknown>,
>(options?: {
  query?: UseQueryOptions<
    Awaited<ReturnType<typeof getTraduzioniLingueDisponibili>>,
    TError,
    TData
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions = getGetTraduzioniLingueDisponibiliQueryOptions(options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
};
